import { useEffect } from 'react'
import { getTenantBrandingUrls } from '../utils/tenantBrandingUrls'

export interface TenantBrandingAssetsProps {
  /**
   * Base URL of the BizFirst.IAM Consolidated WebApi that serves the tenant branding
   * asset endpoints (see `BaseTenantBrandingAssetsController`). The tenant itself is
   * resolved server-side from the request's `Host` header — this component never needs
   * a tenant slug/ID.
   */
  apiBaseUrl: string
}

/**
 * TenantBrandingAssets Component
 *
 * Mount once near the app root (sibling to `AppHeader`, inside `AppShell` — or wherever
 * an app's own root component wraps its whole tree, e.g. `App.tsx`). On mount, injects a
 * `<link rel="stylesheet">` pointed at the tenant's `theme.css` and a `<script>` tag
 * pointed at the tenant's `main.js` into `document.head`. Both endpoints never 404 (200
 * with an empty body when the tenant has no custom CSS/JS), so no load/error handling is
 * needed here. Cleans both tags up on unmount.
 *
 * Renders nothing visible itself. Does NOT handle the tenant logo — use
 * `useTenantLogoUrl()` for that (a plain `<img src>`, no DOM injection needed).
 *
 * @example
 * ```tsx
 * <ThemeProvider defaultTheme="dark">
 *   <TenantBrandingAssets apiBaseUrl={config.iamApiBaseUrl} />
 *   <AppShell />
 * </ThemeProvider>
 * ```
 */
export function TenantBrandingAssets({ apiBaseUrl }: TenantBrandingAssetsProps): null {
  useEffect(() => {
    const { themeCssUrl, scriptJsUrl } = getTenantBrandingUrls(apiBaseUrl)

    const link = document.createElement('link')
    link.rel = 'stylesheet'
    link.href = themeCssUrl
    link.dataset['tenantBranding'] = 'theme-css'
    document.head.appendChild(link)

    const script = document.createElement('script')
    script.src = scriptJsUrl
    script.async = true
    script.dataset['tenantBranding'] = 'main-js'
    document.head.appendChild(script)

    return () => {
      link.remove()
      script.remove()
    }
  }, [apiBaseUrl])

  return null
}

export default TenantBrandingAssets
